import React, { useEffect } from 'react';
import { Theme, Typography, Grid, Box, Stack } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { ReceiptApi, ItemObject, TransactionReceiptInfo } from 'common/types';
import classNames from 'classnames';
import { getPricingString, getAddressStringFromLocation, notNullish } from 'common/utils/common';
import useShopFormat from 'hooks/useShopFormat';
import { useTranslation } from 'services/localization/useTranslation';
import { LocaleHelpers } from 'common/locales/helpers';
import { getTotalItemPricing } from 'common/modules/atoms/pricing';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { useSelector } from 'react-redux';
import { getCurrencyObjectFromCurrencyCode } from 'common/utils/currencyUtils';
import PriceBreakdown from 'common/components/molecules/PriceBreakdown';
import { getTaxBreakdownFromItems, getTaxPercentFromTaxRate } from 'common/modules/atoms/taxes';
import moment from 'moment-timezone';
import RentleLogo from 'common/components/RentleLogo';
export interface Props {
	receipt: ReceiptApi;
}

const PaymentReceipt = (props: Props) => {
	const { receipt } = props;
	const { shopInfo, receiptNumber, items, orderId, transactionsInfo, type, timestamp } = receipt;
	const { localFormat } = useShopFormat();
	const shopCurrency = useSelector(ShopSelectors.getShopCurrency);
	const { t, lang } = useTranslation();
	const classes = useStyles();
	const totalPricing = getTotalItemPricing(items.map((i) => i.pricing));
	const currency = totalPricing.currency
		? getCurrencyObjectFromCurrencyCode(totalPricing.currency, shopCurrency)
		: undefined;

	useEffect(() => {
		const oldDocumentTitle = document.title;
		const formattedTime = moment(timestamp).format('YYYYMMDDHHmm');
		const printedReceiptName = `${receiptNumber}_${formattedTime}`;
		document.title = printedReceiptName;
		return () => {
			document.title = oldDocumentTitle;
		};
	}, [receiptNumber, timestamp]);

	const renderItemRow = (item: ItemObject, index: number) => {
		return (
			<Grid container justifyContent="space-between" key={item.name + index}>
				<Grid item xs>
					<Typography className={classes.receiptFont} variant="body2">
						{item.units ? item.units + 'x ' : ''}
						{item.name}
						{item.durationString ? ', ' + item.durationString : ''}
					</Typography>
				</Grid>
				<Grid item className={classes.noFlex}>
					<Typography className={classes.receiptFont} variant="body2">
						{getPricingString(item.pricing.listPrice, currency)}
					</Typography>
				</Grid>
			</Grid>
		);
	};

	const renderTransactionInfoRow = (transactionInfo: TransactionReceiptInfo) => {
		const transactionId = transactionInfo.transactionId ?? transactionInfo.pspReference;
		return (
			<Grid container justifyContent="space-between" key={transactionInfo.method}>
				<Grid item xs={6}>
					<Typography className={classes.receiptFont} variant="body2">
						{LocaleHelpers.admin(t).paymentMethods[transactionInfo.method] ??
							transactionInfo.method}
					</Typography>
				</Grid>
				<Grid item xs={6}>
					<Typography className={classes.receiptFont} variant="body2" align="right">
						{getPricingString(transactionInfo.amount.value, currency)}
					</Typography>
				</Grid>
				{transactionId && (
					<Grid item xs={12}>
						<Typography
							className={classNames(classes.receiptFont, classes.captionFont)}
							variant="caption"
						>
							{transactionId}
						</Typography>
					</Grid>
				)}
			</Grid>
		);
	};

	const renderTaxSection = (items: ItemObject[]) => {
		const taxBreakdown = getTaxBreakdownFromItems(items);
		const taxRows = taxBreakdown.map((tax) => {
			return (
				<Grid container key={tax.taxRate} justifyContent="space-between">
					<Grid item xs={3}>
						<Typography className={classes.receiptFont} variant="body2" align="left">
							{getTaxPercentFromTaxRate(tax.taxRate).toString()}
						</Typography>
					</Grid>
					<Grid item xs={3}>
						<Typography className={classes.receiptFont} variant="body2" align="right">
							{getPricingString(tax.netAmount)}
						</Typography>
					</Grid>
					<Grid item xs={3}>
						<Typography className={classes.receiptFont} variant="body2" align="right">
							{getPricingString(tax.taxAmount)}
						</Typography>
					</Grid>
					<Grid item xs={3}>
						<Typography className={classes.receiptFont} variant="body2" align="right">
							{getPricingString(tax.total)}
						</Typography>
					</Grid>
				</Grid>
			);
		});
		return (
			<>
				<Grid container justifyContent="space-between">
					<Grid item xs={3}>
						<Typography className={classes.receiptFont} variant="body2" align="left">
							{t('common:taxation.tax', 'Tax')} %
						</Typography>
					</Grid>
					<Grid item xs={3}>
						<Typography className={classes.receiptFont} variant="body2" align="right">
							{t('common:payments.exclVat', 'Excl. Tax')}
						</Typography>
					</Grid>
					<Grid item xs={3}>
						<Typography className={classes.receiptFont} variant="body2" align="right">
							{t('common:taxation.tax', 'Tax')}
						</Typography>
					</Grid>
					<Grid item xs={3}>
						<Typography className={classes.receiptFont} variant="body2" align="right">
							{t('common:payment.total')}
						</Typography>
					</Grid>
				</Grid>
				{taxRows}
			</>
		);
	};

	const renderFooter = () => {
		return (
			<div>
				<Stack alignItems="center">
					<RentleLogo width={60} type="black" />
					<Box pb={0.5} />
					<Typography
						className={classNames(classes.receiptFont, classes.captionFont)}
						variant="body2"
						align="center"
					>
						{orderId}
					</Typography>
				</Stack>
				<Grid container justifyContent="space-between" className={classes.spacingTopSmall}>
					<Grid item>
						<Typography className={classes.receiptFont} align="center" variant="caption">
							{'Powered by Rentle'}
						</Typography>
					</Grid>
					<Grid item>
						<Typography className={classes.receiptFont} align="center" variant="caption">
							{'www.rentle.io'}
						</Typography>
					</Grid>
				</Grid>
			</div>
		);
	};

	return (
		<div className={classes.receiptContainer}>
			<div>
				<Typography
					className={classNames(classes.receiptFont, classes.smFont)}
					variant="body1"
					align="center"
				>
					{shopInfo.name}
				</Typography>
				{shopInfo.visitingAddress && (
					<Typography
						className={classNames(classes.receiptFont, classes.smFont)}
						variant="body2"
						align="center"
					>
						{getAddressStringFromLocation(shopInfo.visitingAddress)}
					</Typography>
				)}
				<Typography
					className={classNames(classes.receiptFont, classes.smFont)}
					variant="body2"
					align="center"
				>
					{shopInfo.phone || ''}
				</Typography>
				<Typography
					className={classNames(classes.receiptFont, classes.smFont)}
					variant="body2"
					align="center"
				>
					{[shopInfo.legalName, shopInfo.businessId].filter(notNullish).join(', ')}
				</Typography>
				<pre>
					<Typography
						className={classNames(classes.receiptFont, classes.smFont)}
						variant="body2"
						align="center"
					>
						{shopInfo.receiptOpenTextfield ?? ''}
					</Typography>
				</pre>
			</div>
			{type === 'refund' && (
				<div className={classes.spacingTopXSmall}>
					<Typography
						className={classNames(classes.receiptFont, classes.smFont)}
						align="left"
						variant="body2"
					>
						<b>{t('common:payment.refund')}</b>
					</Typography>
				</div>
			)}
			<div className={classes.spacingTopXSmall}>
				<Typography
					className={classNames(classes.receiptFont, classes.smFont)}
					align="left"
					variant="body2"
				>
					{t('shop.receipt', 'Receipt')}: {receiptNumber}
				</Typography>
				<Typography
					className={classNames(classes.receiptFont, classes.smFont)}
					variant="body2"
					align="left"
				>
					{localFormat(timestamp, 'DD.MM.YYYY, HH:mm')}
				</Typography>
			</div>
			<div className={classes.spacingTopSmall}>{items.map(renderItemRow)}</div>
			<div className={classes.divider} />
			<PriceBreakdown
				pricing={totalPricing}
				t={t}
				shopCurrency={shopCurrency}
				styling={{
					fontSize: 'normal',
					textRowClass: classes.receiptFont,
					totalRowClass: classes.totalFont,
					totalRowSpacing: 1,
				}}
				lang={lang}
			/>
			<div className={classes.spacingTopSmall}>{renderTaxSection(items)}</div>
			<div className={classes.spacingTopSmall}>
				<Grid container justifyContent="space-between">
					<Grid item xs={6}>
						<Typography className={classes.receiptFont} variant="body2">
							{t('shop.paymentMehtod', 'Payment method')}
						</Typography>
					</Grid>
					<Grid item xs={6}>
						<Typography className={classes.receiptFont} variant="body2" align="right">
							{t('common:payment.total')}
						</Typography>
					</Grid>
				</Grid>
				{transactionsInfo.map(renderTransactionInfoRow)}
			</div>
			<div className={classes.spacingTopSmall}>{renderFooter()}</div>
		</div>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		receiptContainer: {
			width: '92%',
			maxWidth: 500,
			marginLeft: 'auto',
			marginRight: 'auto',
			padding: '4mm 2mm',
			'&*': {
				fontFamily: 'arial',
				'text-rendering': 'geometricPrecision',
				'-webkit-font-smoothing': 'subpixel-antialiased',
			},
			'& p': {
				margin: '2px 0',
			},
			color: 'black !important',
			fontWeight: 500,
			'text-rendering': 'geometricPrecision',
			'-webkit-font-smoothing': 'subpixel-antialiased',
		},
		spacingTopXSmall: {
			paddingTop: '8px',
		},
		spacingTopSmall: {
			paddingTop: '16px',
		},
		receiptFont: {
			fontFamily: ['arial'].join(','),
			color: 'black !important',
			fontWeight: 500,
			fontSize: 12,
			lineHeight: 1.2,
			'text-rendering': 'geometricPrecision',
			'-webkit-font-smoothing': 'subpixel-antialiased',
		},
		totalFont: {
			fontFamily: ['arial'].join(','),
			color: 'black !important',
			fontWeight: 600,
			fontSize: 12,
			lineHeight: 1.2,
			'text-rendering': 'geometricPrecision',
			'-webkit-font-smoothing': 'subpixel-antialiased',
		},
		smFont: {
			fontSize: 12,
		},
		captionFont: {
			fontSize: 10,
		},
		noFlex: {
			flex: '0 0 auto',
			paddingLeft: 16,
		},
		divider: {
			border: '0 none',
			borderTop: '1px dashed black',
			background: 'none',
			height: 1,
			marginTop: '4px',
			paddingTop: '4px',
			'text-rendering': 'geometricPrecision',
			'-webkit-font-smoothing': 'subpixel-antialiased',
		},
	}),
);

// Must export a class to work with react-to-print
class PaymentReceiptContainer extends React.Component<Props> {
	render() {
		return (
			<div>
				<PaymentReceipt {...this.props} />
			</div>
		);
	}
}

export default PaymentReceiptContainer;
